import { useId, useMemo, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeftIcon, PlusIcon, XIcon } from 'lucide-react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from '@/components/ui/combobox';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field, FieldError, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useSingleFlight } from '@/hooks/useSingleFlight';
import { cn } from '@/lib/utils';
import {
  accountsQueryOptions,
  breedsQueryOptions,
  type CreateAnimalInput,
  getPossibleDuplicates,
  OPTION_SET_OBJECT_TYPES,
  pensQueryOptions,
  type PossibleDuplicate,
  useCreateAnimal,
  useCreateAnimals,
  yardsQueryOptions,
} from '@/services/animal-management/api';
import {
  fieldLengthError,
  type LimitedField,
} from '@/services/animal-management/field-limits';
import type { AnimalRow } from '@/services/animal-management/index';
import { useAccessToken } from '@/services/auth';
import { useToast } from '@/services/toast';

import {
  type AddAnimalFormValues,
  buildCreateAnimalInput,
  createAddAnimalSchema,
  isAddFieldRequired,
  isAddFieldVisible,
} from './add-animal-schema';
import {
  OptionSelect,
  ORIGIN_OPTIONS,
  useAnimalStatusOptions,
  useKeyedStringMapOptions,
  YES_NO_OPTIONS,
} from './AnimalFieldInputs';
import {
  applyBulkResults,
  buildBulkCreateInputs,
  BULK_ROW_FIELDS,
  BULK_TEMPLATE_HEADERS,
  type BulkAddRow,
  emptyBulkRow,
  isBlankRow,
  isBulkRowField,
  rowProblem,
} from './bulk-add-rows';
import { BulkAddRows } from './BulkAddRows';
import { LookupCombobox } from './LookupCombobox';
import { OptionSetSelect } from './OptionSetSelect';
import { PossibleDuplicatesPanel } from './PossibleDuplicatesPanel';

type AddAnimalDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  /** Called when the user picks an existing animal from the duplicate warning. */
  onOpenExisting?: (animal: AnimalRow) => void;
  /**
   * Bulk Add: the form holds values shared by the batch and a grid holds the
   * identifying fields of each animal.
   */
  bulk?: boolean;
};

type AddField = Parameters<typeof isAddFieldVisible>[1];

const ERROR_FIELD_MAP: Record<string, keyof AddAnimalFormValues> = {
  metalTag: 'rfidTag',
  registration: 'registrationNumber',
  pedigreeName: 'name',
  tag: 'tag',
  birth: 'birthDate',
  breedId: 'breedId',
  ownerId: 'ownerId',
  yardId: 'yardId',
  penId: 'penId',
  weight: 'weight',
  opuProgram: 'opuProgram',
  programCategory: 'programCategory',
  pregnant: 'pregnant',
  received: 'receivedDate',
  shipOn: 'shipDate',
  origin: 'origin',
  hairSample: 'hairSample',
  heiferCow: 'heiferCow',
  lastDrugUsedId: 'lastDrugUsedId',
  client: 'client',
  colorId: 'colorId',
  ocvStatusId: 'ocvStatusId',
  recipientStatusId: 'recipientStatusId',
  sex: 'sex',
};

export function AddAnimalDialog({
  open,
  onOpenChange,
  onSuccess,
  onOpenExisting,
  bulk = false,
}: AddAnimalDialogProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const createAnimalMutation = useCreateAnimal();
  const createAnimalsMutation = useCreateAnimals();
  const { run: singleFlight, busy: saving } = useSingleFlight();
  const [rows, setRows] = useState<BulkAddRow[]>(() => [emptyBulkRow()]);

  const [breedSearch, setBreedSearch] = useState('');
  const [ownerSearch, setOwnerSearch] = useState('');
  const [yardSearch, setYardSearch] = useState('');
  const [penSearch, setPenSearch] = useState('');
  const token = useAccessToken();
  const [duplicates, setDuplicates] = useState<PossibleDuplicate[] | null>(
    null,
  );
  const [pendingInput, setPendingInput] = useState<CreateAnimalInput | null>(
    null,
  );
  const [overrideReason, setOverrideReason] = useState('');

  const { data: breeds = [] } = useQuery(
    breedsQueryOptions(token, { search: breedSearch }),
  );
  const { data: accounts = [] } = useQuery(
    accountsQueryOptions(token, { search: ownerSearch }),
  );
  const { data: yards = [] } = useQuery(
    yardsQueryOptions(token, { search: yardSearch }),
  );
  const { data: pens = [] } = useQuery(
    pensQueryOptions(token, { search: penSearch }),
  );

  const recipientStatuses = useAnimalStatusOptions('recipient');
  const ocvStatuses = useAnimalStatusOptions('ocv');
  const clients = useKeyedStringMapOptions(
    OPTION_SET_OBJECT_TYPES.recipient,
    'tog_client',
  );
  const sexes = useKeyedStringMapOptions(
    OPTION_SET_OBJECT_TYPES.calf,
    'tog_sex',
  );
  const heiferCows = useKeyedStringMapOptions(
    OPTION_SET_OBJECT_TYPES.donor,
    'tog_heifercow',
  );

  const schema = useMemo(() => {
    // In Bulk Add the identifying fields are checked per row instead.
    const unavailable = new Set<AddField>(bulk ? BULK_ROW_FIELDS : []);
    if (recipientStatuses.unavailable) unavailable.add('recipientStatusId');
    if (ocvStatuses.unavailable) unavailable.add('ocvStatusId');
    if (clients.unavailable) unavailable.add('client');
    if (sexes.unavailable) unavailable.add('sex');
    return createAddAnimalSchema({ unavailable });
  }, [
    bulk,
    recipientStatuses.unavailable,
    ocvStatuses.unavailable,
    clients.unavailable,
    sexes.unavailable,
  ]);

  const form = useForm<AddAnimalFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      animalType: undefined,
      nameTag: '',
      name: '',
      tag: '',
      breedId: '',
      rfidTag: '',
      birthDate: undefined,
      ownerId: '',
      yardId: '',
      receivedDate: undefined,
      weight: undefined,
      penId: '',
      registrationNumber: '',
      shipDate: undefined,
      opuProgram: '',
      togId: '',
      programCategory: '',
      pregnant: 'No',
      ivfEtStatus: '',
      origin: '',
      hairSample: '',
      heiferCow: '',
      lastDrugUsedId: '',
      client: '',
      colorId: '',
      ocvStatusId: '',
      recipientStatusId: '',
      sex: '',
    },
  });

  const animalType = form.watch('animalType');
  // Over-long values show inline while typing rather than waiting for submit.
  const checkLength = (name: LimitedField, value: string) => {
    if (
      fieldLengthError(animalType, name, value) ||
      form.getFieldState(name).invalid
    )
      void form.trigger(name);
  };
  type VisibleField = Parameters<typeof isAddFieldVisible>[1];
  const visible = (field: VisibleField) =>
    isAddFieldVisible(animalType, field) && !(bulk && isBulkRowField(field));
  const req = (field: VisibleField) =>
    isAddFieldRequired(animalType, field) ? '*' : '';

  const clearDuplicates = () => {
    setDuplicates(null);
    setPendingInput(null);
    setOverrideReason('');
  };

  const resetDialog = () => {
    form.reset();
    clearDuplicates();
    setRows([emptyBulkRow()]);
  };

  const handleClose = () => {
    resetDialog();
    onOpenChange(false);
  };

  const handleOpenExisting = (animal: AnimalRow) => {
    handleClose();
    onOpenExisting?.(animal);
  };

  const submit = async (
    input: CreateAnimalInput,
    data: AddAnimalFormValues,
  ) => {
    const result = await createAnimalMutation.mutateAsync(input);

    if (!result.success) {
      const possibleDuplicates = getPossibleDuplicates(result);
      if (possibleDuplicates.length > 0 && !input.duplicateOverrideReason) {
        setDuplicates(possibleDuplicates);
        setPendingInput(input);
        setOverrideReason('');
        return;
      }
      clearDuplicates();

      // Errors on fields the form doesn't show would otherwise leave the
      // dialog open with no feedback, so they go to a toast instead.
      const unshown: string[] = [];
      for (const [backendField, message] of Object.entries(
        result.errors ?? {},
      )) {
        const formField =
          backendField === 'name'
            ? data.animalType === 'Calf'
              ? 'name'
              : 'nameTag'
            : ERROR_FIELD_MAP[backendField];
        if (
          formField &&
          (formField === 'animalType' ||
            isAddFieldVisible(data.animalType, formField as AddField))
        ) {
          form.setError(formField, { type: 'server', message });
        } else {
          unshown.push(message);
        }
      }
      if (!result.errors || unshown.length > 0) {
        toast.show({
          severity: 'error',
          summary: t('common.error', 'Error'),
          detail:
            result.message ??
            unshown[0] ??
            t('animalManagement.saveFailed', 'The animal could not be saved.'),
          life: 8000,
        });
      }
      return;
    }

    toast.show({
      severity: 'success',
      summary: t('common.success', 'Success'),
      detail: t(
        'animalManagement.animalCreated',
        'Animal was added successfully.',
      ),
    });
    resetDialog();
    onOpenChange(false);
    onSuccess();
  };

  const describeProblem = (
    problem: NonNullable<ReturnType<typeof rowProblem>>,
  ) =>
    problem.kind === 'missing'
      ? t('animalManagement.bulkAdd.missing', 'Required: {fields}', {
          fields: problem.fields
            .map((field) => BULK_TEMPLATE_HEADERS[field])
            .join(', '),
        })
      : problem.kind === 'tooLong'
        ? t(
            'animalManagement.bulkAdd.tooLong',
            '{field} is too long. Use {max} characters or fewer.',
            { field: BULK_TEMPLATE_HEADERS[problem.field], max: problem.max },
          )
        : problem.kind === 'birthDate'
          ? t(
              'animalManagement.bulkAdd.invalidBirthDate',
              'Enter the birth date as YYYY-MM-DD or M/D/YYYY.',
            )
          : t(
              'animalManagement.bulkAdd.reasonRequired',
              'Give a reason to add this animal anyway, or remove the row.',
            );

  const submitBulk = async (data: AddAnimalFormValues) => {
    const filled = rows.filter((row) => !isBlankRow(row));
    if (!filled.length) {
      toast.show({
        severity: 'error',
        summary: t('common.error', 'Error'),
        detail: t(
          'animalManagement.bulkAdd.noRows',
          'Add at least one animal.',
        ),
      });
      return;
    }

    let invalid = false;
    const checked = filled.map((row) => {
      const problem = rowProblem(data.animalType, row);
      if (problem) invalid = true;
      return { ...row, error: problem ? describeProblem(problem) : null };
    });
    setRows(checked);
    if (invalid) return;

    const results = await createAnimalsMutation.mutateAsync(
      buildBulkCreateInputs(data, checked),
    );
    const { remaining, created } = applyBulkResults(checked, results);
    if (created > 0) onSuccess();

    if (!remaining.length) {
      toast.show({
        severity: 'success',
        summary: t('common.success', 'Success'),
        detail: t(
          'animalManagement.bulkAdd.created',
          '{count} animal(s) added.',
          { count: created },
        ),
      });
      resetDialog();
      onOpenChange(false);
      return;
    }

    setRows(remaining);
    toast.show({
      severity: 'warn',
      summary: t(
        'animalManagement.bulkAdd.partial',
        '{created} of {total} animals added',
        { created, total: checked.length },
      ),
      detail: t(
        'animalManagement.bulkAdd.needsAttention',
        '{count} row(s) need attention.',
        { count: remaining.length },
      ),
      life: 8000,
    });
  };

  const onSubmit = singleFlight((data: AddAnimalFormValues) =>
    bulk ? submitBulk(data) : submit(buildCreateAnimalInput(data), data),
  );

  const createAnyway = singleFlight(() => {
    const reason = overrideReason.trim();
    if (!pendingInput || !reason) return;
    return submit(
      { ...pendingInput, duplicateOverrideReason: reason },
      form.getValues(),
    );
  });

  const isSaving =
    saving ||
    form.formState.isSubmitting ||
    createAnimalMutation.isPending ||
    createAnimalsMutation.isPending;

  const formId = useId();

  return (
    <Dialog
      open={open}
      // #39988: a stray click outside would discard the form, including a
      // pending duplicate warning. Close with Cancel, Back, X or Esc.
      disablePointerDismissal
      onOpenChange={(newOpen) => {
        if (!newOpen) {
          resetDialog();
        }
        onOpenChange(newOpen);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {bulk
              ? t('animalManagement.bulkAdd.title', 'Bulk Add Animals')
              : t('animalManagement.addNewAnimal', 'Add New Animal')}
          </DialogTitle>
        </DialogHeader>

        <DialogBody
          as="form"
          id={formId}
          onSubmit={form.handleSubmit(onSubmit) as React.FormEventHandler}
          className={cn('flex flex-col gap-4', duplicates && 'hidden')}
        >
          <p className="text-muted-foreground text-sm">
            {t(
              'animalManagement.selectAnimalType',
              'Select animal type and enter required information',
            )}
          </p>

          <div className="rounded-lg border border-blue-100 bg-blue-50 p-4">
            <h3 className="mb-3 text-base font-semibold text-blue-900">
              {t('animalManagement.step1Title', 'Step 1: Select Animal Type')}
            </h3>
            <Controller
              name="animalType"
              control={form.control}
              render={({ field, fieldState }) => (
                <Field data-invalid={fieldState.invalid} className="w-fit">
                  <FieldLabel htmlFor="add-animal-animalType">
                    {t('animalManagement.columns.animalType', 'Animal Type')}*
                  </FieldLabel>
                  <Select value={field.value ?? ''} onChange={field.onChange}>
                    <SelectTrigger
                      id="add-animal-animalType"
                      aria-invalid={fieldState.invalid}
                    >
                      <SelectValue
                        placeholder={t(
                          'animalManagement.selectAnimalTypePlaceholder',
                          'Select animal type...',
                        )}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Donor">Donor</SelectItem>
                      <SelectItem value="Recipient">Recipient</SelectItem>
                      <SelectItem value="Calf">Calf</SelectItem>
                      <SelectItem value="LOPU Donor" disabled>
                        LOPU Donor
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  {fieldState.invalid && (
                    <FieldError errors={[fieldState.error]} />
                  )}
                </Field>
              )}
            />
          </div>

          {animalType && (
            <>
              <div className="rounded-lg border border-gray-200 bg-white p-4">
                <h3 className="mb-3 text-base font-semibold text-blue-900">
                  {t(
                    'animalManagement.step2Title',
                    'Step 2: Basic Information',
                  )}
                </h3>
                <div className="grid grid-cols-4 gap-x-4 gap-y-3">
                  {visible('nameTag') && (
                    <Controller
                      name="nameTag"
                      control={form.control}
                      render={({ field, fieldState }) => (
                        <Field data-invalid={fieldState.invalid}>
                          <FieldLabel htmlFor="add-animal-nameTag">
                            {t(
                              'animalManagement.columns.nameTag',
                              'Name (Tag)',
                            )}
                            {req('nameTag')}
                          </FieldLabel>
                          <Input
                            {...field}
                            onChange={(e) => {
                              field.onChange(e);
                              checkLength('nameTag', e.target.value);
                            }}
                            id="add-animal-nameTag"
                            aria-invalid={fieldState.invalid}
                          />
                          {fieldState.invalid && (
                            <FieldError errors={[fieldState.error]} />
                          )}
                        </Field>
                      )}
                    />
                  )}
                  {visible('name') && (
                    <Controller
                      name="name"
                      control={form.control}
                      render={({ field, fieldState }) => (
                        <Field data-invalid={fieldState.invalid}>
                          <FieldLabel htmlFor="add-animal-name">
                            {t('animalManagement.columns.name', 'Name')}
                            {req('name')}
                          </FieldLabel>
                          <Input
                            {...field}
                            onChange={(e) => {
                              field.onChange(e);
                              checkLength('name', e.target.value);
                            }}
                            id="add-animal-name"
                            aria-invalid={fieldState.invalid}
                          />
                          {fieldState.invalid && (
                            <FieldError errors={[fieldState.error]} />
                          )}
                        </Field>
                      )}
                    />
                  )}
                  {visible('tag') && (
                    <Controller
                      name="tag"
                      control={form.control}
                      render={({ field, fieldState }) => (
                        <Field data-invalid={fieldState.invalid}>
                          <FieldLabel htmlFor="add-animal-tag">
                            {t('animalManagement.columns.tag', 'Tag')}
                            {req('tag')}
                          </FieldLabel>
                          <Input
                            {...field}
                            onChange={(e) => {
                              field.onChange(e);
                              checkLength('tag', e.target.value);
                            }}
                            id="add-animal-tag"
                            aria-invalid={fieldState.invalid}
                          />
                          {fieldState.invalid && (
                            <FieldError errors={[fieldState.error]} />
                          )}
                        </Field>
                      )}
                    />
                  )}
                  {visible('breedId') && (
                    <Controller
                      name="breedId"
                      control={form.control}
                      render={({ field, fieldState }) => (
                        <Field data-invalid={fieldState.invalid}>
                          <FieldLabel htmlFor="add-animal-breedId">
                            {t('animalManagement.columns.breed', 'Breed')}
                            {req('breedId')}
                          </FieldLabel>
                          <Combobox
                            items={breeds}
                            autoComplete="none"
                            value={
                              breeds.find((b) => b.id === field.value) ?? null
                            }
                            onValueChange={(option) => {
                              field.onChange(option?.id ?? '');
                            }}
                            itemToStringValue={(option) => option.id}
                            itemToStringLabel={(option) => option.name}
                          >
                            <ComboboxInput
                              id="add-animal-breedId"
                              aria-invalid={fieldState.invalid}
                              className="h-full w-full"
                              placeholder={t(
                                'animalManagement.columns.breed',
                                'Breed',
                              )}
                              showClear
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>,
                              ) => setBreedSearch(e.target.value)}
                            />
                            <ComboboxContent>
                              <ComboboxEmpty>
                                {t('common.noItemsFound', 'No items found.')}
                              </ComboboxEmpty>
                              <ComboboxList>
                                {(option: (typeof breeds)[number]) => (
                                  <ComboboxItem key={option.id} value={option}>
                                    {option.name}
                                  </ComboboxItem>
                                )}
                              </ComboboxList>
                            </ComboboxContent>
                          </Combobox>
                          {fieldState.invalid && (
                            <FieldError errors={[fieldState.error]} />
                          )}
                        </Field>
                      )}
                    />
                  )}

                  {visible('rfidTag') && (
                    <Controller
                      name="rfidTag"
                      control={form.control}
                      render={({ field, fieldState }) => (
                        <Field data-invalid={fieldState.invalid}>
                          <FieldLabel htmlFor="add-animal-rfidTag">
                            {t('animalManagement.columns.rfidTag', 'RFID Tag')}
                            {req('rfidTag')}
                          </FieldLabel>
                          <Input
                            {...field}
                            onChange={(e) => {
                              field.onChange(e);
                              checkLength('rfidTag', e.target.value);
                            }}
                            id="add-animal-rfidTag"
                            aria-invalid={fieldState.invalid}
                          />
                          {fieldState.invalid && (
                            <FieldError errors={[fieldState.error]} />
                          )}
                        </Field>
                      )}
                    />
                  )}
                  {visible('birthDate') && (
                    <Controller
                      name="birthDate"
                      control={form.control}
                      render={({ field, fieldState }) => (
                        <Field data-invalid={fieldState.invalid}>
                          <FieldLabel htmlFor="add-animal-birthDate">
                            {t(
                              'animalManagement.columns.birthDate',
                              'Birth Date',
                            )}
                            {req('birthDate')}
                          </FieldLabel>
                          <DatePicker
                            value={field.value}
                            onChange={field.onChange}
                            className="h-8"
                          />
                          {fieldState.invalid && (
                            <FieldError errors={[fieldState.error]} />
                          )}
                        </Field>
                      )}
                    />
                  )}
                  {visible('ownerId') && (
                    <Controller
                      name="ownerId"
                      control={form.control}
                      render={({ field, fieldState }) => (
                        <Field data-invalid={fieldState.invalid}>
                          <FieldLabel htmlFor="add-animal-ownerId">
                            {t('animalManagement.columns.ranch', 'Ranch')}
                            {req('ownerId')}
                          </FieldLabel>
                          <Combobox
                            items={accounts}
                            autoComplete="none"
                            value={
                              accounts.find((a) => a.id === field.value) ?? null
                            }
                            onValueChange={(option) => {
                              field.onChange(option?.id ?? '');
                            }}
                            itemToStringValue={(option) => option.id}
                            itemToStringLabel={(option) => option.name}
                          >
                            <ComboboxInput
                              id="add-animal-ownerId"
                              aria-invalid={fieldState.invalid}
                              className="h-full w-full"
                              placeholder={t(
                                'animalManagement.columns.ranch',
                                'Ranch',
                              )}
                              showClear
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>,
                              ) => setOwnerSearch(e.target.value)}
                            />
                            <ComboboxContent>
                              <ComboboxEmpty>
                                {t('common.noItemsFound', 'No items found.')}
                              </ComboboxEmpty>
                              <ComboboxList>
                                {(option: (typeof accounts)[number]) => (
                                  <ComboboxItem key={option.id} value={option}>
                                    {option.name}
                                    {option.code ? ` (${option.code})` : ''}
                                  </ComboboxItem>
                                )}
                              </ComboboxList>
                            </ComboboxContent>
                          </Combobox>
                          {fieldState.invalid && (
                            <FieldError errors={[fieldState.error]} />
                          )}
                        </Field>
                      )}
                    />
                  )}
                  {visible('yardId') && (
                    <Controller
                      name="yardId"
                      control={form.control}
                      render={({ field, fieldState }) => (
                        <Field data-invalid={fieldState.invalid}>
                          <FieldLabel htmlFor="add-animal-yardId">
                            {t('animalManagement.columns.yard', 'Yard')}
                            {req('yardId')}
                          </FieldLabel>
                          <Combobox
                            items={yards}
                            autoComplete="none"
                            value={
                              yards.find((y) => y.id === field.value) ?? null
                            }
                            onValueChange={(option) => {
                              field.onChange(option?.id ?? '');
                            }}
                            itemToStringValue={(option) => option.id}
                            itemToStringLabel={(option) => option.name}
                          >
                            <ComboboxInput
                              id="add-animal-yardId"
                              aria-invalid={fieldState.invalid}
                              className="h-full w-full"
                              placeholder={t(
                                'animalManagement.columns.yard',
                                'Yard',
                              )}
                              showClear
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>,
                              ) => setYardSearch(e.target.value)}
                            />
                            <ComboboxContent>
                              <ComboboxEmpty>
                                {t('common.noItemsFound', 'No items found.')}
                              </ComboboxEmpty>
                              <ComboboxList>
                                {(option: (typeof yards)[number]) => (
                                  <ComboboxItem key={option.id} value={option}>
                                    {option.name}
                                  </ComboboxItem>
                                )}
                              </ComboboxList>
                            </ComboboxContent>
                          </Combobox>
                          {fieldState.invalid && (
                            <FieldError errors={[fieldState.error]} />
                          )}
                        </Field>
                      )}
                    />
                  )}

                  {visible('receivedDate') && (
                    <Controller
                      name="receivedDate"
                      control={form.control}
                      render={({ field, fieldState }) => (
                        <Field data-invalid={fieldState.invalid}>
                          <FieldLabel htmlFor="add-animal-receivedDate">
                            {t(
                              'animalManagement.columns.receivedDate',
                              'Received Date',
                            )}
                            {req('receivedDate')}
                          </FieldLabel>
                          <DatePicker
                            value={field.value}
                            onChange={field.onChange}
                            className="h-8"
                          />
                          {fieldState.invalid && (
                            <FieldError errors={[fieldState.error]} />
                          )}
                        </Field>
                      )}
                    />
                  )}
                  {visible('weight') && (
                    <Controller
                      name="weight"
                      control={form.control}
                      render={({ field, fieldState }) => (
                        <Field data-invalid={fieldState.invalid}>
                          <FieldLabel htmlFor="add-animal-weight">
                            {t('animalManagement.columns.weight', 'Weight')}{' '}
                            (lbs)
                            {req('weight')}
                          </FieldLabel>
                          <Input
                            id="add-animal-weight"
                            type="number"
                            {...field}
                            value={field.value ?? ''}
                            onChange={(e) =>
                              field.onChange(e.target.valueAsNumber || 0)
                            }
                            aria-invalid={fieldState.invalid}
                          />
                          {fieldState.invalid && (
                            <FieldError errors={[fieldState.error]} />
                          )}
                        </Field>
                      )}
                    />
                  )}
                  {visible('penId') && (
                    <Controller
                      name="penId"
                      control={form.control}
                      render={({ field, fieldState }) => (
                        <Field data-invalid={fieldState.invalid}>
                          <FieldLabel htmlFor="add-animal-penId">
                            {t('animalManagement.columns.housing', 'Housing')}
                            {req('penId')}
                          </FieldLabel>
                          <Combobox
                            items={pens}
                            autoComplete="none"
                            value={
                              pens.find((p) => p.id === field.value) ?? null
                            }
                            onValueChange={(option) => {
                              field.onChange(option?.id ?? '');
                            }}
                            itemToStringValue={(option) => option.id}
                            itemToStringLabel={(option) => option.name}
                          >
                            <ComboboxInput
                              id="add-animal-penId"
                              aria-invalid={fieldState.invalid}
                              className="h-full w-full"
                              placeholder={t(
                                'animalManagement.columns.housing',
                                'Housing',
                              )}
                              showClear
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>,
                              ) => setPenSearch(e.target.value)}
                            />
                            <ComboboxContent>
                              <ComboboxEmpty>
                                {t('common.noItemsFound', 'No items found.')}
                              </ComboboxEmpty>
                              <ComboboxList>
                                {(option: (typeof pens)[number]) => (
                                  <ComboboxItem key={option.id} value={option}>
                                    {option.name}
                                  </ComboboxItem>
                                )}
                              </ComboboxList>
                            </ComboboxContent>
                          </Combobox>
                          {fieldState.invalid && (
                            <FieldError errors={[fieldState.error]} />
                          )}
                        </Field>
                      )}
                    />
                  )}

                  {visible('registrationNumber') && (
                    <Controller
                      name="registrationNumber"
                      control={form.control}
                      render={({ field, fieldState }) => (
                        <Field data-invalid={fieldState.invalid}>
                          <FieldLabel htmlFor="add-animal-registrationNumber">
                            {t(
                              'animalManagement.registrationNumber',
                              'Registration Number',
                            )}
                            {req('registrationNumber')}
                          </FieldLabel>
                          <Input
                            {...field}
                            value={field.value ?? ''}
                            onChange={(e) => {
                              field.onChange(e);
                              checkLength('registrationNumber', e.target.value);
                            }}
                            id="add-animal-registrationNumber"
                            aria-invalid={fieldState.invalid}
                          />
                          {fieldState.invalid && (
                            <FieldError errors={[fieldState.error]} />
                          )}
                        </Field>
                      )}
                    />
                  )}
                  {visible('shipDate') && (
                    <Controller
                      name="shipDate"
                      control={form.control}
                      render={({ field, fieldState }) => (
                        <Field data-invalid={fieldState.invalid}>
                          <FieldLabel htmlFor="add-animal-shipDate">
                            {t(
                              'animalManagement.columns.shipDate',
                              'Ship Date',
                            )}
                            {req('shipDate')}
                          </FieldLabel>
                          <DatePicker
                            value={field.value}
                            onChange={field.onChange}
                            className="h-8"
                          />
                        </Field>
                      )}
                    />
                  )}
                  {visible('origin') && (
                    <Controller
                      name="origin"
                      control={form.control}
                      render={({ field, fieldState }) => (
                        <Field data-invalid={fieldState.invalid}>
                          <FieldLabel htmlFor="add-animal-origin">
                            {t('animalManagement.columns.origin', 'Origin')}
                            {req('origin')}
                          </FieldLabel>
                          <OptionSelect
                            id="add-animal-origin"
                            options={ORIGIN_OPTIONS}
                            value={field.value}
                            onChange={field.onChange}
                            invalid={fieldState.invalid}
                          />
                          {fieldState.invalid && (
                            <FieldError errors={[fieldState.error]} />
                          )}
                        </Field>
                      )}
                    />
                  )}
                  {visible('colorId') && (
                    <Controller
                      name="colorId"
                      control={form.control}
                      render={({ field, fieldState }) => (
                        <Field data-invalid={fieldState.invalid}>
                          <FieldLabel htmlFor="add-animal-colorId">
                            {t('animalManagement.columns.color', 'Color')}
                            {req('colorId')}
                          </FieldLabel>
                          <LookupCombobox
                            id="add-animal-colorId"
                            kind="color"
                            value={field.value || null}
                            onChange={(id) => field.onChange(id ?? '')}
                            placeholder={t(
                              'animalManagement.columns.color',
                              'Color',
                            )}
                            invalid={fieldState.invalid}
                          />
                          {fieldState.invalid && (
                            <FieldError errors={[fieldState.error]} />
                          )}
                        </Field>
                      )}
                    />
                  )}
                  {visible('sex') && (
                    <Controller
                      name="sex"
                      control={form.control}
                      render={({ field, fieldState }) => (
                        <Field data-invalid={fieldState.invalid}>
                          <FieldLabel htmlFor="add-animal-sex">
                            {t('animalManagement.columns.sex', 'Sex')}
                            {sexes.unavailable ? '' : req('sex')}
                          </FieldLabel>
                          <OptionSelect
                            id="add-animal-sex"
                            options={sexes.options}
                            value={field.value}
                            onChange={field.onChange}
                            invalid={fieldState.invalid}
                            disabled={sexes.isLoading}
                          />
                          {fieldState.invalid && (
                            <FieldError errors={[fieldState.error]} />
                          )}
                        </Field>
                      )}
                    />
                  )}
                  {visible('recipientStatusId') && (
                    <Controller
                      name="recipientStatusId"
                      control={form.control}
                      render={({ field, fieldState }) => (
                        <Field data-invalid={fieldState.invalid}>
                          <FieldLabel htmlFor="add-animal-recipientStatusId">
                            {t(
                              'animalManagement.columns.recipientStatus',
                              'Recip Status',
                            )}
                            {recipientStatuses.unavailable
                              ? ''
                              : req('recipientStatusId')}
                          </FieldLabel>
                          <OptionSelect
                            id="add-animal-recipientStatusId"
                            options={recipientStatuses.options}
                            value={field.value}
                            onChange={field.onChange}
                            invalid={fieldState.invalid}
                            disabled={recipientStatuses.isLoading}
                          />
                          {fieldState.invalid && (
                            <FieldError errors={[fieldState.error]} />
                          )}
                        </Field>
                      )}
                    />
                  )}
                  {visible('ocvStatusId') && (
                    <Controller
                      name="ocvStatusId"
                      control={form.control}
                      render={({ field, fieldState }) => (
                        <Field data-invalid={fieldState.invalid}>
                          <FieldLabel htmlFor="add-animal-ocvStatusId">
                            {t(
                              'animalManagement.columns.ocvStatus',
                              'OCV Status',
                            )}
                            {ocvStatuses.unavailable ? '' : req('ocvStatusId')}
                          </FieldLabel>
                          <OptionSelect
                            id="add-animal-ocvStatusId"
                            options={ocvStatuses.options}
                            value={field.value}
                            onChange={field.onChange}
                            invalid={fieldState.invalid}
                            disabled={ocvStatuses.isLoading}
                          />
                          {fieldState.invalid && (
                            <FieldError errors={[fieldState.error]} />
                          )}
                        </Field>
                      )}
                    />
                  )}
                  {visible('client') && (
                    <Controller
                      name="client"
                      control={form.control}
                      render={({ field, fieldState }) => (
                        <Field data-invalid={fieldState.invalid}>
                          <FieldLabel htmlFor="add-animal-client">
                            {t(
                              'animalManagement.columns.client',
                              'Original Owner',
                            )}
                            {clients.unavailable ? '' : req('client')}
                          </FieldLabel>
                          <OptionSelect
                            id="add-animal-client"
                            options={clients.options}
                            value={field.value}
                            onChange={field.onChange}
                            invalid={fieldState.invalid}
                            disabled={clients.isLoading}
                          />
                          {fieldState.invalid && (
                            <FieldError errors={[fieldState.error]} />
                          )}
                        </Field>
                      )}
                    />
                  )}
                  {visible('lastDrugUsedId') && (
                    <Controller
                      name="lastDrugUsedId"
                      control={form.control}
                      render={({ field, fieldState }) => (
                        <Field data-invalid={fieldState.invalid}>
                          <FieldLabel htmlFor="add-animal-lastDrugUsedId">
                            {t(
                              'animalManagement.columns.lastDrugUsed',
                              'Last Drug Used',
                            )}
                            {req('lastDrugUsedId')}
                          </FieldLabel>
                          <LookupCombobox
                            id="add-animal-lastDrugUsedId"
                            kind="drug"
                            value={field.value || null}
                            onChange={(id) => field.onChange(id ?? '')}
                            placeholder={t(
                              'animalManagement.columns.lastDrugUsed',
                              'Last Drug Used',
                            )}
                            invalid={fieldState.invalid}
                          />
                          {fieldState.invalid && (
                            <FieldError errors={[fieldState.error]} />
                          )}
                        </Field>
                      )}
                    />
                  )}
                  {visible('heiferCow') && (
                    <Controller
                      name="heiferCow"
                      control={form.control}
                      render={({ field, fieldState }) => (
                        <Field data-invalid={fieldState.invalid}>
                          <FieldLabel htmlFor="add-animal-heiferCow">
                            {t(
                              'animalManagement.columns.cowHeifer',
                              'Cow/Heifer',
                            )}
                            {req('heiferCow')}
                          </FieldLabel>
                          <OptionSelect
                            id="add-animal-heiferCow"
                            options={heiferCows.options}
                            value={field.value}
                            onChange={field.onChange}
                            invalid={fieldState.invalid}
                            disabled={heiferCows.isLoading}
                          />
                          {fieldState.invalid && (
                            <FieldError errors={[fieldState.error]} />
                          )}
                        </Field>
                      )}
                    />
                  )}
                  {visible('hairSample') && (
                    <Controller
                      name="hairSample"
                      control={form.control}
                      render={({ field, fieldState }) => (
                        <Field data-invalid={fieldState.invalid}>
                          <FieldLabel htmlFor="add-animal-hairSample">
                            {t(
                              'animalManagement.columns.hairSample',
                              'Hair Sample',
                            )}
                            {req('hairSample')}
                          </FieldLabel>
                          <OptionSelect
                            id="add-animal-hairSample"
                            options={YES_NO_OPTIONS}
                            value={field.value}
                            onChange={field.onChange}
                            invalid={fieldState.invalid}
                          />
                          {fieldState.invalid && (
                            <FieldError errors={[fieldState.error]} />
                          )}
                        </Field>
                      )}
                    />
                  )}
                </div>
              </div>

              {animalType === 'Calf' && (
                <div className="rounded-lg border border-green-200 bg-green-50 p-6">
                  <h3 className="mb-2 text-lg font-semibold text-green-800">
                    {t(
                      'animalManagement.calfSpecificInfo',
                      'Calf-Specific Information',
                    )}
                  </h3>
                  <p className="font-medium text-green-800">
                    {t(
                      'animalManagement.noAdditionalFieldsCalves',
                      'No additional fields required for Calves',
                    )}
                  </p>
                  <p className="mt-1 text-sm text-green-600">
                    {t(
                      'animalManagement.basicInfoSufficient',
                      'Basic information is sufficient. Additional fields can be added after conversion.',
                    )}
                  </p>
                </div>
              )}

              {animalType === 'Donor' && (
                <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-6">
                  <h3 className="mb-4 text-lg font-semibold text-yellow-800">
                    {t(
                      'animalManagement.donorSpecificInfo',
                      'Donor-Specific Information',
                    )}
                  </h3>
                  <div className="grid grid-cols-4 gap-x-4 gap-y-3">
                    <Controller
                      name="opuProgram"
                      control={form.control}
                      render={({ field, fieldState }) => (
                        <Field data-invalid={fieldState.invalid}>
                          <FieldLabel htmlFor="add-animal-opuProgram">
                            {t(
                              'animalManagement.columns.opuProgram',
                              'OPU Program',
                            )}
                            {req('opuProgram')}
                          </FieldLabel>
                          <OptionSetSelect
                            id="add-animal-opuProgram"
                            objectTypeCode={OPTION_SET_OBJECT_TYPES.donor}
                            attribute="tog_opuprogram"
                            value={field.value}
                            onChange={field.onChange}
                            invalid={fieldState.invalid}
                          />
                          {fieldState.invalid && (
                            <FieldError errors={[fieldState.error]} />
                          )}
                        </Field>
                      )}
                    />
                    <Controller
                      name="pregnant"
                      control={form.control}
                      render={({ field, fieldState }) => (
                        <Field data-invalid={fieldState.invalid}>
                          <FieldLabel htmlFor="add-animal-pregnant">
                            {t('animalManagement.columns.pregnant', 'Pregnant')}
                          </FieldLabel>
                          <Select {...field} value={field.value ?? 'No'}>
                            <SelectTrigger
                              id="add-animal-pregnant"
                              aria-invalid={fieldState.invalid}
                              className="w-full"
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Yes">Yes</SelectItem>
                              <SelectItem value="No">No</SelectItem>
                            </SelectContent>
                          </Select>
                          {fieldState.invalid && (
                            <FieldError errors={[fieldState.error]} />
                          )}
                        </Field>
                      )}
                    />
                    <Controller
                      name="programCategory"
                      control={form.control}
                      render={({ field, fieldState }) => (
                        <Field data-invalid={fieldState.invalid}>
                          <FieldLabel htmlFor="add-animal-programCategory">
                            {t(
                              'animalManagement.columns.programCategory',
                              'Program Category',
                            )}
                          </FieldLabel>
                          <OptionSetSelect
                            id="add-animal-programCategory"
                            objectTypeCode={OPTION_SET_OBJECT_TYPES.donor}
                            attribute="tog_category"
                            value={field.value}
                            onChange={field.onChange}
                            invalid={fieldState.invalid}
                          />
                          {fieldState.invalid && (
                            <FieldError errors={[fieldState.error]} />
                          )}
                        </Field>
                      )}
                    />
                  </div>
                </div>
              )}
            </>
          )}

          {bulk && animalType && (
            <BulkAddRows
              animalType={animalType}
              rows={rows}
              onRowsChange={setRows}
              disabled={isSaving}
            />
          )}
        </DialogBody>

        {duplicates && (
          <DialogBody>
            <PossibleDuplicatesPanel
              duplicates={duplicates}
              reason={overrideReason}
              onReasonChange={setOverrideReason}
              onOpenExisting={onOpenExisting ? handleOpenExisting : undefined}
            />
          </DialogBody>
        )}

        {duplicates ? (
          <DialogFooter>
            <Button type="button" variant="outline" onClick={clearDuplicates}>
              <ArrowLeftIcon className="mr-2 size-4" />
              {t('common.back', 'Back')}
            </Button>
            <Button
              type="button"
              onClick={createAnyway}
              disabled={!overrideReason.trim() || isSaving}
            >
              <PlusIcon className="mr-2 size-4" />
              {t('animalManagement.duplicates.createAnyway', 'Create anyway')}
            </Button>
          </DialogFooter>
        ) : (
          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              <XIcon className="mr-2 size-4" />
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button
              type="submit"
              form={formId}
              disabled={!animalType || isSaving}
            >
              <PlusIcon className="mr-2 size-4" />
              {bulk
                ? t('animalManagement.bulkAdd.submit', 'Add {count} Animals', {
                    count: rows.filter((row) => !isBlankRow(row)).length,
                  })
                : t('animalManagement.addAnimal', 'Add Animal')}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
